import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import styles from './styles.module.scss'
import validationStyles from '../shared/validation.module.scss'
import labelStyles from '../shared/fieldLabel.module.scss'
import fieldWrapperStyles from '../shared/fieldWrapper.module.scss'

export type RawInputFieldProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'id' | 'name' | 'type'
> & {
  id: string
  fieldName?: string
  label: string
  type?: 'text' | 'email' | 'password' | 'number' | 'tel'
  value: string | number | undefined
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  icon?: React.ReactNode
  required?: boolean
  disabled?: boolean
  errorText?: string | null
  helperMessage?: string
}

const NUMBERS_ONLY_ALLOWED = /\d/

export function RawInputField({
  id,
  fieldName,
  label,
  type = 'text',
  value,
  onChange,
  icon,
  required = false,
  disabled = false,
  placeholder,
  errorText,
  onFocus,
  onBlur,
  className,
  helperMessage,
  ...rest
}: RawInputFieldProps) {
  const [focused, setFocused] = useState(false)
  const [paddingPercent, setPaddingPercent] = useState<number | null>(null)

  const iconRef = useRef<HTMLSpanElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const updatePadding = () => {
      if (iconRef.current && inputRef.current) {
        const iconW = iconRef.current.getBoundingClientRect().width
        const inputW = inputRef.current.getBoundingClientRect().width
        if (inputW > 0) setPaddingPercent((iconW / inputW) * 100)
      }
    }
    updatePadding()
    window.addEventListener('resize', updatePadding)
    return () => window.removeEventListener('resize', updatePadding)
  }, [icon])

  const hasValue =
    value !== undefined &&
    value !== null &&
    (typeof value === 'number' || String(value).trim().length > 0)

  const isActive = focused

  const inputClasses = classNames(
    styles.InputFieldInput,
    {
      [styles.withIcon]: !!icon,
      [styles.disabled]: disabled,
      [styles.focused]: isActive,
      [styles.error]: !!errorText && !disabled
    },
    className
  )

  const labelClasses = classNames({
    [labelStyles.disabled]: disabled,
    [labelStyles.focused]: isActive,
    [labelStyles.required]: required
  })

  const handleOnBeforeInput = (e: React.CompositionEvent<HTMLInputElement>) => {
    if (!NUMBERS_ONLY_ALLOWED.test(e.data) && type === 'number') {
      e.preventDefault()
    }
  }

  return (
    <div className={fieldWrapperStyles.FieldWrapper}>
      <label
        htmlFor={id}
        className={`${labelStyles.FieldLabel} ${
          labelStyles.FieldLabel__fluid
        } ${isActive ? labelStyles.focused : ''}`}
        style={
          paddingPercent !== null
            ? { paddingLeft: `${paddingPercent + 2}%` } // +2% visual offset
            : undefined
        }
      >
        <span
          className={`${labelStyles.FieldLabel__placeholder} ${labelClasses}`}
        >
          {placeholder || label}
        </span>
        <span className={`${labelStyles.FieldLabel__name} ${labelClasses}`}>
          {label}
        </span>
      </label>

      {icon && (
        <span
          ref={iconRef}
          className={`${styles.InputFieldIcon} ${
            disabled ? styles.disabled : ''
          }`}
        >
          {icon}
        </span>
      )}

      <input
        ref={inputRef}
        id={id}
        name={fieldName ?? id}
        type={type}
        aria-label={label}
        value={hasValue ? value : ''}
        onChange={onChange}
        onFocus={e => {
          if (!disabled) setFocused(true)
          onFocus?.(e)
        }}
        onBlur={e => {
          if (!disabled) setFocused(false)
          onBlur?.(e)
        }}
        className={inputClasses}
        autoComplete="off"
        disabled={disabled}
        onBeforeInput={handleOnBeforeInput}
        style={
          paddingPercent !== null
            ? { paddingLeft: `${paddingPercent + 2}%` }
            : undefined
        }
        placeholder={placeholder}
        {...(type == 'number' && { inputMode: 'numeric', pattern: '[0-9]*' })}
        {...rest}
      />

      {helperMessage && !errorText && (
        <p className={styles.HelperText}>{helperMessage}</p>
      )}

      {errorText && !disabled && (
        <div className={validationStyles.ErrorMessage} role="alert">
          {errorText}
        </div>
      )}
    </div>
  )
}
